import React, { useState, useEffect } from 'react';
import {
  Menu,
  X,
  Leaf,
  Sun,
  Droplets,
  MapPin,
  Camera,
  MessageCircle,
  Share2,
  ChevronDown,
  Star,
  Package,
  Truck,
  Heart,
} from 'lucide-react';
import './App.css';

// ── 商品データ ────────────────────────────────────────
const products = [
  {
    id: 1,
    name: '宮崎きゅうり 旬の詰め合わせ',
    description: '朝露を纏った、パリッと弾ける歯応え。\n宮崎の太陽と清らかな水が育てた逸品。',
    price: '¥1,280',
    unit: '（約10本入り）',
    badge: '人気No.1',
    badgeColor: 'bg-orange-500',
    bgFrom: 'from-green-50',
    bgTo: 'to-emerald-100',
    accentColor: 'text-green-700',
    icon: '🥒',
    image: '/product-cucumber.webp',
    shopUrl: 'https://kajiru.base.shop/items/143448442',
    baseId: '143448442',
  },
  {
    id: 2,
    name: '完熟トマト 朝採りセット',
    description: '完熟まで畑で待ち続けた、甘みと酸味の\nハーモニー。そのまま食べても絶品です。',
    price: '¥1,580',
    unit: '（約1kg）',
    badge: '旬の味',
    badgeColor: 'bg-red-500',
    bgFrom: 'from-red-50',
    bgTo: 'to-orange-50',
    accentColor: 'text-red-600',
    icon: '🍅',
    image: '/product-tomato.webp',
    baseId: '143497027',
  },
  {
    id: 3,
    name: '宮崎完熟マンゴー',
    description: '太陽の恵みを凝縮した宮崎の宝。\nとろける甘さと芳醇な香りをご自宅で。',
    price: '¥3,800',
    unit: '（2玉入り）',
    badge: '限定品',
    badgeColor: 'bg-yellow-500',
    bgFrom: 'from-yellow-50',
    bgTo: 'to-orange-100',
    accentColor: 'text-orange-600',
    icon: '🥭',
    image: '/product-mango.webp',
    baseId: '143497189',
  },
  {
    id: 4,
    name: '旬の野菜セット おまかせ便',
    description: '農園おすすめの旬野菜を毎週厳選。\n農家から直接届く、本物の鮮度をどうぞ。',
    price: '¥2,480',
    unit: '（8〜10種類）',
    badge: '定期便対応',
    badgeColor: 'bg-green-600',
    bgFrom: 'from-green-50',
    bgTo: 'to-lime-100',
    accentColor: 'text-green-700',
    icon: '🧺',
    image: '/product-vegset.webp',
    baseId: '143497301',
  },
];

// ── カートフック（LocalStorage + BASEカートURL生成）────
function useCart() {
  const STORAGE_KEY = 'kajiru_cart';
  const read = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
    catch { return {}; }
  };
  const [items, setItems] = useState(read);

  useEffect(() => {
    const sync = () => setItems(read());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = (baseId) => {
    setItems(prev => {
      const next = { ...prev, [baseId]: (prev[baseId] || 0) + 1 };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const totalCount = Object.values(items).reduce((s, n) => s + n, 0);

  const cartUrl = (() => {
    const ids = Object.keys(items).filter(k => items[k] > 0);
    if (!ids.length) return null;
    if (ids.length === 1) return `https://kajiru.base.shop/items/${ids[0]}`;
    return 'https://kajiru.base.shop/';
  })();

  return { items, addItem, totalCount, cartUrl };
}

// ── ヘッダー ──────────────────────────────────────────
function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Concept', href: '#concept' },
    { label: 'Products', href: '#products' },
    { label: 'About', href: '#about' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* ロゴ */}
          <a href="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center shadow-md group-hover:bg-green-700 transition-colors">
              <Leaf size={16} className="text-white" />
            </div>
            <span
              className={`font-bold text-lg tracking-wide transition-colors ${
                isScrolled ? 'text-green-800' : 'text-white drop-shadow'
              }`}
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              宮崎かじる農園（架空プロジェクト）
            </span>
          </a>

          {/* デスクトップナビ */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium tracking-wider hover:text-green-500 transition-colors ${
                  isScrolled ? 'text-gray-700' : 'text-white/90'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* ハンバーガー */}
          <div className="flex items-center">
            <button
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X size={22} className={isScrolled ? 'text-gray-700' : 'text-white'} />
              ) : (
                <Menu size={22} className={isScrolled ? 'text-gray-700' : 'text-white'} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* モバイルメニュー */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setIsMenuOpen(false)}
                className="block py-2.5 px-3 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors font-medium"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

// ── ヒーローセクション ────────────────────────────────
function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* 背景画像 */}
      <div className="absolute inset-0 bg-stone-900">
        <img
          src="/hero.webp"
          alt="宮崎の畑と野菜"
          className="w-full h-full object-contain"
        />
        {/* グラデーションオーバーレイ：下から上に暗くしてテキストを読みやすく */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/10" />
      </div>

      {/* コンテンツ */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 rounded-full px-4 py-1.5 text-sm font-medium mb-8 tracking-wide">
          <MapPin size={14} />
          宮崎県産 産地直送
        </div>

        <h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
          style={{
            fontFamily: "'Noto Serif JP', serif",
            textShadow: '0 4px 24px rgba(0,0,0,0.4)',
          }}
        >
          宮崎を、かじる。
          <span className="block text-2xl sm:text-3xl md:text-4xl mt-3 font-normal text-green-200 leading-relaxed">
            音が響くほどの鮮度を、産地直送で。
          </span>
        </h1>

        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="h-px w-12 bg-green-400/60" />
          <Leaf size={16} className="text-green-300" />
          <div className="h-px w-12 bg-green-400/60" />
        </div>

        <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed mb-10">
          きゅうりの「パリッ」という音だけでなく、野菜の瑞々しさや果物の弾ける感覚を<br className="hidden sm:block" />
          「かじる」という一言に凝縮しました。宮崎の自然を丸ごと食べているような贅沢感を。
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#products"
            className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full shadow-lg transition-all hover:-translate-y-0.5 text-sm tracking-wide"
          >
            商品を見る
          </a>
          <a
            href="#concept"
            className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-medium rounded-full transition-all text-sm tracking-wide"
          >
            私たちのこだわり
          </a>
        </div>
      </div>

      {/* スクロールインジケーター */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/50 animate-bounce">
        <span className="text-xs tracking-widest">SCROLL</span>
        <ChevronDown size={16} />
      </div>
    </section>
  );
}

// ── コンセプトセクション ─────────────────────────────
function ConceptSection() {
  const features = [
    {
      icon: <Sun size={28} className="text-orange-500" />,
      title: '日本一の日照時間',
      desc: '宮崎県は全国でも屈指の日照時間を誇ります。太陽の恵みをたっぷり浴びた野菜たちは、糖度と栄養価が驚くほど高く育ちます。',
    },
    {
      icon: <Droplets size={28} className="text-blue-500" />,
      title: '清らかな水と大地',
      desc: '霧島山系から流れる清冽な水と、豊かなミネラルを含んだ火山灰土壌が、農産物に独特の風味と瑞々しさを与えてくれます。',
    },
    {
      icon: <Leaf size={28} className="text-green-600" />,
      title: '朝採りの鮮度',
      desc: '収穫したその日のうちに発送。あなたの食卓に届く頃も、まるで畑で採ったばかりのような「パリッ」とした食感をお届けします。',
    },
  ];

  return (
    <section id="concept" className="py-24 bg-stone-50">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-green-600 text-sm font-semibold tracking-widest uppercase">— Concept —</span>
          <h2
            className="text-3xl sm:text-4xl font-bold text-gray-800 mt-3 mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            なぜ「かじる」なのか。
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed text-base sm:text-lg">
            「かじる」という言葉には、ただ食べるだけでなく、<strong className="text-green-700">生命力を直接受け取る</strong>ようなイメージがあります。
            宮崎の大地が育んだ野菜や果物を、できる限りそのままの姿でお届けしたい——それが私たちの原点です。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center mb-4">
                {f.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-3">{f.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-br from-green-700 to-emerald-800 rounded-3xl p-10 text-center text-white">
          <p
            className="text-xl sm:text-2xl font-bold leading-relaxed mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            「野菜が美味しいのは当たり前。<br />
            その感動を、そのまま届けたい。」
          </p>
          <p className="text-green-200 text-sm">— 農園代表 宮崎かじる</p>
        </div>
      </div>
    </section>
  );
}

// ── 商品カード ────────────────────────────────────────
function ProductCard({ product, addToCart, cartItems }) {
  const [ripplePos, setRipplePos] = useState(null);
  const [floatKey, setFloatKey] = useState(0);
  const [showFloat, setShowFloat] = useState(false);
  const inCart = product.baseId ? (cartItems?.[product.baseId] || 0) : 0;

  const handleAddClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRipplePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setTimeout(() => setRipplePos(null), 700);
    setFloatKey(k => k + 1);
    setShowFloat(true);
    setTimeout(() => setShowFloat(false), 900);
    addToCart(product.baseId);
  };

  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      <div className={`relative h-52 bg-gradient-to-br ${product.bgFrom} ${product.bgTo} flex items-center justify-center overflow-hidden`}>
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 p-2"
          />
        ) : (
          <span className="text-7xl group-hover:scale-110 transition-transform duration-300 select-none">
            {product.icon}
          </span>
        )}
        <span className={`absolute top-3 left-3 ${product.badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full shadow`}>
          {product.badge}
        </span>
        <button className="absolute top-3 right-3 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow transition-colors">
          <Heart size={15} className="text-gray-400 hover:text-red-400 transition-colors" />
        </button>
        {!product.image && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-gray-400 whitespace-nowrap">
            📸 商品写真が入ります
          </div>
        )}
      </div>

      <div className="p-5">
        <h3 className="font-bold text-gray-800 text-base mb-2 leading-snug">{product.name}</h3>
        <p className="text-gray-500 text-xs leading-relaxed mb-4 whitespace-pre-line">{product.description}</p>

        <div className="flex items-baseline gap-1 mb-3">
          <span className={`text-2xl font-bold ${product.accentColor}`}>{product.price}</span>
          <span className="text-gray-400 text-xs">{product.unit}</span>
        </div>

        <div className="flex items-center gap-1 mb-4">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={12} className="text-yellow-400 fill-yellow-400" />
          ))}
          <span className="text-gray-400 text-xs ml-1">（レビュー多数）</span>
        </div>

        {product.baseId ? (
          inCart > 0 ? (
            /* カートに入っている → BASEカートへ直行 */
            <a
              href={`https://kajiru.base.shop/items/${product.baseId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm bg-green-600 hover:bg-green-700 text-white transition-all duration-200"
            >
              今すぐ購入する
            </a>
          ) : (
            /* 未追加 → LocalStorageに追加 + ASMRアニメーション */
            <button
              onClick={handleAddClick}
              className="relative overflow-hidden w-full py-3 rounded-xl font-bold text-sm bg-gray-900 hover:bg-green-700 text-white transition-all duration-200 active:scale-95"
            >
              カートに入れる
              {ripplePos && (
                <span
                  className="kajiru-ripple"
                  style={{ left: ripplePos.x, top: ripplePos.y }}
                />
              )}
              {showFloat && <span key={floatKey} className="kajiru-float">+1</span>}
            </button>
          )
        ) : (
          /* BASE未連携の商品 */
          <button
            disabled
            className="w-full py-3 rounded-xl font-bold text-sm bg-gray-100 text-gray-400 cursor-not-allowed"
          >
            近日公開
          </button>
        )}
      </div>
    </div>
  );
}

// ── 商品セクション ────────────────────────────────────
function ProductsSection({ addToCart, cartItems }) {
  return (
    <section id="products" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="text-green-600 text-sm font-semibold tracking-widest uppercase">— Products —</span>
          <h2
            className="text-3xl sm:text-4xl font-bold text-gray-800 mt-3 mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            産地直送 オンラインショップ
          </h2>
          <p className="text-gray-500 text-base">朝採れの鮮度そのままに、あなたの食卓へ。</p>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-12 max-w-2xl mx-auto">
          {[
            { icon: <Truck size={18} />, text: '送料無料\n3,000円以上' },
            { icon: <Package size={18} />, text: '朝採り当日\n発送' },
            { icon: <Star size={18} />, text: '鮮度保証\n満足保証' },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5 bg-green-50 rounded-xl py-3 px-2 text-green-700">
              {item.icon}
              <span className="text-xs font-medium text-center leading-tight whitespace-pre-line">{item.text}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} addToCart={addToCart} cartItems={cartItems} />
          ))}
        </div>

        <div className="text-center mt-12">
          <button className="px-10 py-3.5 border-2 border-green-700 text-green-700 hover:bg-green-700 hover:text-white font-bold rounded-full transition-all text-sm tracking-wide">
            すべての商品を見る
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Aboutセクション ───────────────────────────────────
function AboutSection() {
  return (
    <section id="about" className="py-24 bg-gradient-to-br from-green-800 to-emerald-900 text-white overflow-hidden">
      <div className="max-w-5xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-green-300 text-sm font-semibold tracking-widest uppercase">— About —</span>
            <h2
              className="text-3xl sm:text-4xl font-bold mt-3 mb-6 leading-tight"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              宮崎の大地と、<br />生産者の誠実さ。
            </h2>
            <p className="text-green-100 leading-relaxed mb-5">
              私たちは宮崎県西都市で、三代にわたって野菜を育ててきました。
              化学肥料に頼らず、土の力を信じた農法を守り続けています。
            </p>
            <p className="text-green-100 leading-relaxed mb-8">
              宮崎は年間平均気温17℃、日照時間は全国屈指。
              霧島連山の清水と、太陽の恵みが、野菜ひとつひとつに凝縮されています。
              「おいしい」と感じてもらえる瞬間が、私たちの原動力です。
            </p>

            <div className="grid grid-cols-3 gap-4">
              {[
                { num: '30+', label: '年の農業歴' },
                { num: '2,000+', label: '年間お届け世帯' },
                { num: '98%', label: '顧客満足度' },
              ].map((stat, i) => (
                <div key={i} className="text-center bg-white/10 rounded-xl py-4">
                  <div className="text-2xl font-bold text-white">{stat.num}</div>
                  <div className="text-green-300 text-xs mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="aspect-video rounded-3xl overflow-hidden border-2 border-white/20 shadow-2xl">
              <img
                src="/about-farm.webp"
                alt="宮崎かじる農園・生産者"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-orange-500/30 rounded-full blur-2xl" />
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-green-400/20 rounded-full blur-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── フッター ─────────────────────────────────────────
function Footer() {
  const links = {
    ショップ: ['商品一覧', 'おすすめセット', '定期便', '旬の便り'],
    サービス: ['よくある質問', '送料について', '返品・交換', 'お問い合わせ'],
    農園について: ['農園紹介', '栽培へのこだわり', 'SDGsへの取り組み', 'メディア掲載'],
  };

  return (
    <footer id="contact" className="bg-gray-900 text-gray-300">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <Leaf size={16} className="text-white" />
              </div>
              <span className="font-bold text-white text-lg" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                宮崎かじる農園
              </span>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              宮崎の大地が育んだ、<br />本物の鮮度をお届けします。
            </p>
            <div className="flex gap-3">
              {[
                { Icon: Camera, label: 'Instagram' },
                { Icon: MessageCircle, label: 'Twitter/X' },
                { Icon: Share2, label: 'Facebook' },
              ].map(({ Icon, label }) => (
                <a
                  key={label}
                  href="#!"
                  aria-label={label}
                  className="w-9 h-9 bg-gray-800 hover:bg-green-600 rounded-full flex items-center justify-center transition-colors"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {Object.entries(links).map(([category, items]) => (
            <div key={category}>
              <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">{category}</h4>
              <ul className="space-y-2.5">
                {items.map((item) => (
                  <li key={item}>
                    <a href="#!" className="text-gray-400 hover:text-green-400 text-sm transition-colors">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-800 py-5">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-gray-500 text-sm">© 2024 宮崎かじる農園 All Rights Reserved.</p>
          <div className="flex gap-5 text-xs text-gray-500">
            <a href="#!" className="hover:text-gray-300 transition-colors">プライバシーポリシー</a>
            <a href="#!" className="hover:text-gray-300 transition-colors">特定商取引法に基づく表記</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── メインApp ────────────────────────────────────────
export default function App() {
  const { items, addItem } = useCart();

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <ConceptSection />
        <ProductsSection addToCart={addItem} cartItems={items} />
        <AboutSection />
      </main>
      <Footer />
    </div>
  );
}
